import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import Login from '@mui/icons-material/esm/Login';
import Logout from '@mui/icons-material/esm/Logout';
import Refresh from '@mui/icons-material/esm/Refresh';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import { normalizeRole } from '../../routes/fieldAccess';
import attendanceService, { AttendancePayload, AttendanceRecord } from '../../services/api/attendanceService';
import timesheetService, { Worker } from '../../services/api/timesheetService';
import { cachedRecords, cachedWorkers, enqueueOfflineAttendance, mergeById } from '../../services/offline/fieldOfflineForms';
import { findAuthenticatedWorker } from './workerFieldInsights';

const today = () => new Date().toISOString().slice(0, 10);

export default function AttendancePage() {
  const { activeProjectId, projects } = useSelector((state: RootState) => state.projects);
  const { user } = useSelector((state: RootState) => state.auth);
  const [attendanceDate, setAttendanceDate] = useState(today());
  const [rows, setRows] = useState<AttendanceRecord[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [expandedDate, setExpandedDate] = useState<string | false>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const role = normalizeRole(user?.role);
  const workerSelfService = ['construction', 'worker', 'construction_worker'].includes(role);
  const supervisorReadOnly = role === 'supervisor';
  const allowedTeamIds = useMemo(
    () => (workerSelfService ? (user?.teamIds ?? []).filter(Boolean) : []),
    [user?.teamIds, workerSelfService]
  );
  const activeCrewId = allowedTeamIds[0] ?? '';
  const workerAttendanceDate = today();
  const projectName = projects.find((project) => project.id === activeProjectId)?.name ?? 'Active project';
  const currentWorker = useMemo(
    () => findAuthenticatedWorker(workers, user?.firstName, user?.lastName),
    [user?.firstName, user?.lastName, workers]
  );
  const workerTeamName = currentWorker?.team?.name ?? 'Crew not linked';

  const filterWorkersForRole = useCallback(
    (workerRows: Worker[]) => workerRows.filter((worker) =>
      worker.isActive !== false
      && (!workerSelfService || !allowedTeamIds.length || allowedTeamIds.includes(worker.teamId ?? ''))
    ),
    [allowedTeamIds, workerSelfService]
  );

  const filterAttendanceForRole = useCallback(
    (attendanceRows: AttendanceRecord[], workerRows: Worker[]) => {
      if (workerSelfService) {
        const self = findAuthenticatedWorker(workerRows, user?.firstName, user?.lastName);
        if (self) return attendanceRows.filter((row) => row.workerId === self.id);
        return attendanceRows.filter((row) => !row.worker?.teamId || allowedTeamIds.includes(row.worker.teamId));
      }

      if (supervisorReadOnly) {
        const workerIds = new Set(workerRows.map((worker) => worker.id));
        return attendanceRows.filter((row) => workerIds.has(row.workerId));
      }

      return attendanceRows;
    },
    [allowedTeamIds, supervisorReadOnly, user?.firstName, user?.lastName, workerSelfService]
  );

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    try {
      setLoading(true);
      setError(null);
      const [cachedAttendance, cachedWorkerRows] = await Promise.all([
        cachedRecords<AttendanceRecord>('attendance_record', activeProjectId),
        cachedWorkers(),
      ]);
      const scopedCachedWorkers = filterWorkersForRole(cachedWorkerRows);
      const scopedCachedAttendance = filterAttendanceForRole(cachedAttendance, scopedCachedWorkers);

      if (!navigator.onLine) {
        const visibleAttendanceDate = workerSelfService ? workerAttendanceDate : attendanceDate;
        const visibleRows = supervisorReadOnly
          ? sortRows(scopedCachedAttendance)
          : sortRows(scopedCachedAttendance.filter((row) => row.attendanceDate === visibleAttendanceDate));
        setRows(visibleRows);
        setWorkers(scopedCachedWorkers);
        return;
      }

      const attendanceParams = {
        projectId: activeProjectId,
        ...(supervisorReadOnly ? {} : { attendanceDate: workerSelfService ? workerAttendanceDate : attendanceDate }),
        ...(activeCrewId ? { teamId: activeCrewId } : {}),
      };
      const [serverRows, workerRows] = await Promise.all([
        attendanceService.list(attendanceParams),
        timesheetService.listWorkers({ active: true, ...(activeCrewId ? { teamId: activeCrewId } : {}) }),
      ]);
      const mergedWorkers = filterWorkersForRole(mergeById(workerRows, scopedCachedWorkers));
      const mergedAttendance = filterAttendanceForRole(mergeById(serverRows, scopedCachedAttendance), mergedWorkers);
      const visibleAttendanceDate = workerSelfService ? workerAttendanceDate : attendanceDate;
      setRows(sortRows(supervisorReadOnly ? mergedAttendance : mergedAttendance.filter((row) => row.attendanceDate === visibleAttendanceDate)));
      setWorkers(mergedWorkers);
    } catch (err: any) {
      const [cachedAttendance, cachedWorkerRows] = await Promise.all([
        cachedRecords<AttendanceRecord>('attendance_record', activeProjectId),
        cachedWorkers(),
      ]);
      const scopedCachedWorkers = filterWorkersForRole(cachedWorkerRows);
      const scopedCachedAttendance = filterAttendanceForRole(cachedAttendance, scopedCachedWorkers);
      const visibleAttendanceDate = workerSelfService ? workerAttendanceDate : attendanceDate;
      const visibleRows = supervisorReadOnly
        ? sortRows(scopedCachedAttendance)
        : sortRows(scopedCachedAttendance.filter((row) => row.attendanceDate === visibleAttendanceDate));
      setRows(visibleRows);
      setWorkers(scopedCachedWorkers);
      setError(cachedAttendance.length || cachedWorkerRows.length ? null : err.response?.data?.error || err.response?.data?.message || 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [activeCrewId, activeProjectId, attendanceDate, filterAttendanceForRole, filterWorkersForRole, supervisorReadOnly, workerAttendanceDate, workerSelfService]);

  useEffect(() => {
    void load();
  }, [load]);

  const presentCount = useMemo(() => rows.filter((row) => row.scanInAt && !row.scanOutAt).length, [rows]);
  const todaysWorkerRecord = useMemo(
    () => currentWorker ? rows.find((row) => row.workerId === currentWorker.id && row.attendanceDate === workerAttendanceDate) ?? null : null,
    [currentWorker, rows, workerAttendanceDate]
  );
  const workerIsClockedIn = Boolean(todaysWorkerRecord?.scanInAt && !todaysWorkerRecord.scanOutAt);
  const rowsByDate = useMemo(() => groupRowsByDate(rows), [rows]);
  const workerById = useMemo(() => new Map(workers.map((worker) => [worker.id, worker])), [workers]);

  const recordSelfAttendance = useCallback(async (direction: 'in' | 'out') => {
    if (!activeProjectId || !currentWorker) return;

    try {
      const now = new Date().toISOString();
      const payload: AttendancePayload = {
        projectId: activeProjectId,
        workerId: currentWorker.id,
        attendanceDate: workerAttendanceDate,
        method: 'manual',
        scanInAt: direction === 'in' ? now : undefined,
        scanOutAt: direction === 'out' ? now : undefined,
      };

      let record: AttendanceRecord;
      if (navigator.onLine) {
        record = await attendanceService.record(payload);
      } else {
        record = await enqueueOfflineAttendance(payload, currentWorker);
      }

      setRows((current) => sortRows(mergeById(current, [{ ...record, worker: record.worker ?? currentWorker }])));
      setNotice(`${currentWorker.name} logged ${direction === 'in' ? 'in' : 'out'}${navigator.onLine ? '' : ' offline'}.`);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to record attendance');
    }
  }, [activeProjectId, currentWorker, workerAttendanceDate]);

  if (!activeProjectId) {
    return <Box><PageHeader title="Attendance" /><Alert severity="info">Select a project to use attendance.</Alert></Box>;
  }

  return (
    <Box>
      <PageHeader
        title="Attendance"
        subtitle={supervisorReadOnly
          ? `${projectName} - ${presentCount} currently in`
          : `${projectName} - ${workerSelfService ? workerAttendanceDate : attendanceDate}`}
        actions={<IconButton onClick={load} disabled={loading} aria-label="Refresh attendance"><Refresh /></IconButton>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {workerSelfService && (
        <Stack spacing={2}>
          {!currentWorker && (
            <Alert severity="warning">
              This login is not linked to a worker record by name yet. Attendance cannot be recorded until the worker record matches this account.
            </Alert>
          )}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>
                  Worker attendance
                </Typography>
                <Typography sx={{ fontWeight: 900 }}>
                  {currentWorker?.name ?? (`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Worker account')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {workerTeamName} {currentWorker?.badgeCode ? `- Code ${currentWorker.badgeCode}` : ''}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 150 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>
                  Today
                </Typography>
                <Typography sx={{ fontWeight: 900 }}>{formatDateLabel(workerAttendanceDate)}</Typography>
              </Box>
              <Chip
                color={workerIsClockedIn ? 'success' : 'default'}
                label={workerIsClockedIn ? 'Logged in' : todaysWorkerRecord?.scanOutAt ? 'Logged out' : 'Not logged in'}
                sx={{ fontWeight: 900 }}
              />
              <Button
                variant="contained"
                startIcon={<Login />}
                disabled={!currentWorker || workerIsClockedIn}
                onClick={() => recordSelfAttendance('in')}
                sx={{ minWidth: 120, fontWeight: 900 }}
              >
                Log In
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Logout />}
                disabled={!currentWorker || !workerIsClockedIn}
                onClick={() => recordSelfAttendance('out')}
                sx={{ minWidth: 120, fontWeight: 900 }}
              >
                Log Out
              </Button>
            </Stack>
          </Paper>

          <AttendanceTable rows={rows} workers={workers} />
        </Stack>
      )}

      {supervisorReadOnly && (
        <Paper sx={{ borderRadius: 1, overflow: 'hidden' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}
          >
            <Box>
              <Typography fontWeight={900}>Supervised attendance</Typography>
              <Typography variant="body2" color="text.secondary">Worker log-ins from the crews assigned to this supervisor.</Typography>
            </Box>
            <Chip size="small" color="primary" label={`${presentCount} currently in`} />
          </Stack>
          <Box sx={{ p: 1.5 }}>
            {rowsByDate.map(([date, dateRows]) => {
              const datePresent = dateRows.filter((row) => row.scanInAt && !row.scanOutAt).length;
              return (
                <Accordion
                  key={date}
                  expanded={expandedDate === date}
                  onChange={(_, expanded) => setExpandedDate(expanded ? date : false)}
                  disableGutters
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={900}>{formatDateLabel(date)}</Typography>
                        <Typography variant="caption" color="text.secondary">{dateRows.length} attendance record{dateRows.length === 1 ? '' : 's'}</Typography>
                      </Box>
                      <Chip size="small" color={datePresent ? 'success' : 'default'} label={`${datePresent} in`} sx={{ fontWeight: 800 }} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <AttendanceTable rows={dateRows} workers={workers} workerById={workerById} compact />
                  </AccordionDetails>
                </Accordion>
              );
            })}
            {!rowsByDate.length && <Alert severity="info">No worker log-ins have been recorded for the supervised crews.</Alert>}
          </Box>
        </Paper>
      )}

      {!workerSelfService && !supervisorReadOnly && (
        <Stack spacing={2}>
          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <TextField
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={attendanceDate}
              onChange={(event) => setAttendanceDate(event.target.value)}
              sx={{ minWidth: 180 }}
            />
          </Paper>
          <AttendanceTable rows={rows} workers={workers} />
        </Stack>
      )}
    </Box>
  );
}

function AttendanceTable({
  rows,
  workers,
  workerById,
  compact = false,
}: {
  rows: AttendanceRecord[];
  workers: Worker[];
  workerById?: Map<string, Worker>;
  compact?: boolean;
}) {
  const lookup = workerById ?? new Map(workers.map((worker) => [worker.id, worker]));
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1, bgcolor: '#fff' }}>
      <Table size={compact ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            <TableCell>Worker</TableCell>
            <TableCell>Crew / Code</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>In</TableCell>
            <TableCell>Out</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const lookupWorker = lookup.get(row.workerId);
            const worker = lookupWorker ? { ...row.worker, ...lookupWorker } : row.worker;
            const crewName = lookupWorker?.team?.name ?? 'Crew not linked';
            return (
              <TableRow key={row.id}>
                <TableCell>
                  <Typography fontWeight={800}>{worker?.name ?? row.workerId}</Typography>
                  {worker?.trade && <Typography variant="caption" color="text.secondary">{worker.trade}</Typography>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{crewName}</Typography>
                  <Typography variant="caption" color="text.secondary">{worker?.badgeCode ?? '-'}</Typography>
                </TableCell>
                <TableCell>{formatDateLabel(row.attendanceDate)}</TableCell>
                <TableCell>{formatTime(row.scanInAt)}</TableCell>
                <TableCell>{row.scanOutAt ? formatTime(row.scanOutAt) : '-'}</TableCell>
                <TableCell>
                  <Chip size="small" color={row.scanOutAt ? 'default' : 'success'} label={row.scanOutAt ? 'out' : 'in'} />
                </TableCell>
              </TableRow>
            );
          })}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={6}>
                <Alert severity="info">No attendance records found.</Alert>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}

function sortRows(rows: AttendanceRecord[]): AttendanceRecord[] {
  return [...rows].sort((a, b) =>
    b.attendanceDate.localeCompare(a.attendanceDate) || a.scanInAt.localeCompare(b.scanInAt)
  );
}

function groupRowsByDate(rows: AttendanceRecord[]): Array<[string, AttendanceRecord[]]> {
  const groups = new Map<string, AttendanceRecord[]>();
  sortRows(rows).forEach((row) => {
    groups.set(row.attendanceDate, [...(groups.get(row.attendanceDate) ?? []), row]);
  });
  return [...groups.entries()].sort(([a], [b]) => b.localeCompare(a));
}

function formatDateLabel(value?: string | null): string {
  if (!value) return '-';
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day))
      .toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(value?: string | null): string {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
